import { useEffect, useRef } from 'react';
import { LAST_ACTIVITY_KEY } from '@/config/session';

/** Persist shared activity at most this often (storage writes fan out to every tab). */
const WRITE_THROTTLE_MS = 5_000;

function readShared(): number {
  try { return Number(localStorage.getItem(LAST_ACTIVITY_KEY)) || 0; } catch { return 0; }
}
function writeShared(at: number): void {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(at)); } catch { /* storage unavailable */ }
}

/**
 * Idle tracking shared by every open tab. The idle clock runs from the latest
 * activity in ANY tab (via localStorage + the `storage` event), so an unused
 * background tab never signs out a session the user is working in elsewhere.
 * `onWarn` fires `warnBeforeMs` before the deadline. `onTimeout` fires at the
 * deadline only after re-checking that no tab has been active since.
 * `onActive` fires when activity (here or in another tab) resumes.
 */
export function useIdleTimeout({ idleMs, warnBeforeMs, onWarn, onTimeout, onActive, enabled }: {
  idleMs: number;
  warnBeforeMs: number;
  onWarn: () => void;
  onTimeout: () => void;
  onActive?: () => void;
  enabled: boolean;
}): { reset: () => void } {
  const warnTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastLocal = useRef(0);
  const lastWritten = useRef(0);
  const handlers = useRef({ onWarn, onTimeout, onActive });
  handlers.current = { onWarn, onTimeout, onActive };
  const api = useRef<{ activity: () => void }>({ activity: () => undefined });

  useEffect(() => {
    const clear = () => {
      if (warnTimer.current) clearTimeout(warnTimer.current);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
    if (!enabled) { clear(); return; }

    const lastActivity = () => Math.max(lastLocal.current, readShared());
    const schedule = () => {
      clear();
      const deadline = lastActivity() + idleMs;
      const now = Date.now();
      warnTimer.current = setTimeout(() => handlers.current.onWarn(), Math.max(0, deadline - warnBeforeMs - now));
      idleTimer.current = setTimeout(() => {
        // Another tab may have been active since this timer was armed.
        if (lastActivity() + idleMs > Date.now() + 250) { schedule(); handlers.current.onActive?.(); return; }
        handlers.current.onTimeout();
      }, Math.max(0, deadline - now));
    };
    const activity = () => {
      const now = Date.now();
      lastLocal.current = now;
      if (now - lastWritten.current >= WRITE_THROTTLE_MS) { lastWritten.current = now; writeShared(now); }
      schedule();
      handlers.current.onActive?.();
    };
    const fromOtherTab = (e: StorageEvent) => {
      if (e.key !== LAST_ACTIVITY_KEY) return;
      schedule();
      handlers.current.onActive?.();
    };
    api.current.activity = activity;

    // Mounting (sign-in, reload, opening a tab) is itself user activity.
    lastWritten.current = 0;
    activity();
    const events: (keyof WindowEventMap)[] = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, activity, { passive: true }));
    document.addEventListener('visibilitychange', activity);
    window.addEventListener('storage', fromOtherTab);
    return () => {
      clear();
      events.forEach((e) => window.removeEventListener(e, activity));
      document.removeEventListener('visibilitychange', activity);
      window.removeEventListener('storage', fromOtherTab);
    };
  }, [enabled, idleMs, warnBeforeMs]);

  return { reset: () => api.current.activity() };
}
